import * as THREE from 'three';
import { cloneGLBIfReady, onGLBReady } from '../../engine/gltf';

// INSTANCED buildable GLB visuals (owner 2026-07-12: "instance buildables with blueprints -- as
// performant as possible"). Every placed piece of one (tier, pieceId) shares ONE InstancedMesh per
// GLB submesh: geometry + material upload once, and a 200-piece base of the owner's 40-70k-vertex
// exports renders in a handful of draw calls instead of hundreds. The per-piece scene-graph clone
// path this replaces cost one draw per submesh per piece AND made every aim/floor raycast walk the
// full vertex set (fixed earlier with pick proxies -- kept here: the instanced meshes never raycast;
// each placed piece's group holds only its invisible proxy box).
//
// The FIT is template-level: fitGLBToBox's stretch-to-procedural-bounds math runs ONCE per file
// (every piece of a kind shares the same target box), captured into each submesh's local matrix.
// A piece's instance matrix = its group matrix (position/yaw/build-anim scale) x that local matrix,
// so the place animation just pushes fresh matrices while it runs.
//
// Removal uses swap-with-last so the buffers stay dense; capacity grows by doubling. Templates load
// through the shared GLB cache (cloneGLBIfReady/onGLBReady); a piece placed before its file resolves
// keeps its procedural placeholder and swaps the moment the template lands.

const START_CAPACITY = 32;

interface Sub { geometry: THREE.BufferGeometry; material: THREE.Material | THREE.Material[]; local: THREE.Matrix4; }

interface FileSet {
  subs: Sub[];
  meshes: THREE.InstancedMesh[]; // 1:1 with subs
  ids: string[];                 // slot -> placedId (dense)
  slots: Map<string, number>;    // placedId -> slot
  capacity: number;
}

const M_TMP = new THREE.Matrix4();

export class BuildGLBInstances {
  readonly group = new THREE.Group();
  private readonly files = new Map<string, FileSet>();                       // template ready + pools live
  private readonly loading = new Set<string>();                              // template requested, not yet built
  private readonly byId = new Map<string, { file: string; g: THREE.Group }>();// every attached piece
  private readonly pending = new Map<string, { file: string; g: THREE.Group; target: THREE.Box3; verticalFix: boolean }>();

  // Adopt a placed piece: its group `g` currently holds the procedural placeholder; `target` is that
  // placeholder's local Box3 (measured untransformed in makePiece -- the same fit contract as before).
  // When the template is ready the placeholder is swapped for the invisible pick proxy and the piece
  // joins the file's instance pools. verticalFix: ceiling_triangle models are authored standing up.
  attach(placedId: string, g: THREE.Group, file: string, target: THREE.Box3, verticalFix: boolean): void {
    this.remove(placedId); // re-attach (upgrade/reskin) replaces cleanly
    const set = this.files.get(file);
    if (set) { this.materialize(placedId, g, file, set, target); return; }
    this.pending.set(placedId, { file, g, target, verticalFix });
    if (this.loading.has(file)) return;
    this.loading.add(file);
    const url = '/buildables/' + file;
    const ready = cloneGLBIfReady(url);
    if (ready) this.buildTemplate(file, ready, target, verticalFix);
    else onGLBReady(url, (scene) => this.buildTemplate(file, scene, target, verticalFix));
  }

  // Refresh one piece's instance matrices from its group transform (the build-anim tick, ~0.45s).
  updateMatrix(placedId: string, g: THREE.Object3D): void {
    const rec = this.byId.get(placedId);
    if (!rec) return;
    const set = this.files.get(rec.file);
    const slot = set?.slots.get(placedId);
    if (!set || slot === undefined) return;
    g.updateMatrix();
    for (let i = 0; i < set.subs.length; i++) {
      M_TMP.multiplyMatrices(g.matrix, set.subs[i].local);
      set.meshes[i].setMatrixAt(slot, M_TMP);
      set.meshes[i].instanceMatrix.needsUpdate = true;
    }
  }

  // Drop a piece from its pools (demolish/crumble/pickup/upgrade). Swap-with-last keeps buffers dense.
  remove(placedId: string): void {
    this.pending.delete(placedId);
    const rec = this.byId.get(placedId);
    if (!rec) return;
    this.byId.delete(placedId);
    const set = this.files.get(rec.file);
    const slot = set?.slots.get(placedId);
    if (!set || slot === undefined) return;
    set.slots.delete(placedId);
    const last = set.ids.length - 1;
    if (slot !== last) {
      const movedId = set.ids[last];
      set.ids[slot] = movedId;
      set.slots.set(movedId, slot);
      for (const m of set.meshes) { m.getMatrixAt(last, M_TMP); m.setMatrixAt(slot, M_TMP); }
    }
    set.ids.pop();
    for (const m of set.meshes) { m.count = set.ids.length; m.instanceMatrix.needsUpdate = true; }
  }

  // --- internals -------------------------------------------------------------------------------

  // Run the fit ONCE against the first requester's target box (identical for every piece of the kind),
  // capture each submesh's {geometry, material, local matrix}, then flush every pending piece.
  private buildTemplate(file: string, scene: THREE.Object3D, target: THREE.Box3, verticalFix: boolean): void {
    if (this.files.has(file)) return;
    if (verticalFix) scene.rotation.x = -Math.PI / 2; // vertical-authored model -> horizontal slab
    const wrap = new THREE.Group();
    wrap.add(scene);
    wrap.updateMatrixWorld(true);
    const src = new THREE.Box3().setFromObject(wrap);
    const ss = src.getSize(new THREE.Vector3()), ts = target.getSize(new THREE.Vector3());
    wrap.scale.set(ts.x / (ss.x || 1), ts.y / (ss.y || 1), ts.z / (ss.z || 1));
    wrap.updateMatrixWorld(true);
    const scaled = new THREE.Box3().setFromObject(wrap);
    wrap.position.add(target.getCenter(new THREE.Vector3()).sub(scaled.getCenter(new THREE.Vector3())));
    wrap.updateMatrixWorld(true);
    const subs: Sub[] = [];
    wrap.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh && m.geometry) subs.push({ geometry: m.geometry, material: m.material, local: m.matrixWorld.clone() });
    });
    if (!subs.length) return; // broken export: pending pieces keep their procedural placeholders
    const set: FileSet = { subs, meshes: [], ids: [], slots: new Map(), capacity: START_CAPACITY };
    for (const sub of subs) set.meshes.push(this.makeMesh(sub, set.capacity));
    this.files.set(file, set);
    this.loading.delete(file);
    for (const [pid, p] of [...this.pending]) {
      if (p.file !== file) continue;
      this.pending.delete(pid);
      this.materialize(pid, p.g, file, set, p.target);
    }
  }

  private makeMesh(sub: Sub, capacity: number): THREE.InstancedMesh {
    const m = new THREE.InstancedMesh(sub.geometry, sub.material, capacity);
    m.count = 0;
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    m.castShadow = true;
    m.receiveShadow = true;
    m.frustumCulled = false; // instances span the whole island: one shared bound would cull wrong
    m.raycast = () => {};    // owner rule: buildable GLB visuals NEVER raycast (pick proxies handle aim)
    this.group.add(m);
    return m;
  }

  private growSet(set: FileSet): void {
    set.capacity *= 2;
    for (let i = 0; i < set.meshes.length; i++) {
      const old = set.meshes[i];
      const fresh = this.makeMesh(set.subs[i], set.capacity);
      (fresh.instanceMatrix.array as Float32Array).set((old.instanceMatrix.array as Float32Array).subarray(0, set.ids.length * 16));
      fresh.count = set.ids.length;
      fresh.instanceMatrix.needsUpdate = true;
      this.group.remove(old);
      old.dispose(); // frees the old instance buffer only -- geometry/material are shared and stay
      set.meshes[i] = fresh;
    }
  }

  // Swap the placeholder for the pick proxy + take a slot in every pool.
  private materialize(placedId: string, g: THREE.Group, file: string, set: FileSet, target: THREE.Box3): void {
    // The placeholder's one-off procedural geometry goes; the proxy box (same bounds, opacity 0,
    // default raycast) is all the piece's own group keeps -- aim/demolish/floor tests hit it cheaply.
    g.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.geometry.dispose(); });
    g.clear();
    const size = target.getSize(new THREE.Vector3()), center = target.getCenter(new THREE.Vector3());
    const proxy = new THREE.Mesh(new THREE.BoxGeometry(Math.max(size.x, 0.01), Math.max(size.y, 0.01), Math.max(size.z, 0.01)), PICK_PROXY_MAT);
    proxy.geometry.userData.stitched = true; // stitchSolid must never jitter the pick box (it runs after the upgrade-path adopt)
    proxy.position.copy(center);
    g.add(proxy);
    if (set.ids.length >= set.capacity) this.growSet(set);
    const slot = set.ids.length;
    set.ids.push(placedId);
    set.slots.set(placedId, slot);
    this.byId.set(placedId, { file, g });
    g.updateMatrix();
    for (let i = 0; i < set.subs.length; i++) {
      M_TMP.multiplyMatrices(g.matrix, set.subs[i].local);
      set.meshes[i].setMatrixAt(slot, M_TMP);
      set.meshes[i].count = set.ids.length;
      set.meshes[i].instanceMatrix.needsUpdate = true;
    }
  }
}

// Invisible (opacity 0, no depth write) but still raycastable -- the shared pick-proxy material.
const PICK_PROXY_MAT = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false });
